/**
 * Overflow-specific mapping between print MPV IDs and service MPV IDs.
 * This mapping is exclusively used for product overflow scenarios where
 * print products need to be redirected to alternative design services.
 */

export interface ServiceMpvMapping {
    printMpvId: string;
    serviceMpvId: string;
}

/**
 * Maps print MPV IDs to their corresponding overflow service MPV IDs.
 * This mapping is used exclusively for product overflow scenarios:
 * - Determining which print products are eligible for overflow
 * - Redirecting overflow traffic to appropriate design services
 * - Building URLs with correct service MPV IDs and options
 */
export const OVERFLOW_PRODUCTS_MAPPING: ServiceMpvMapping[] = [
    // Flyers mapping
    { printMpvId: 'flyers', serviceMpvId: 'flyerdesign' },
    { printMpvId: 'flyersandLeaflets', serviceMpvId: 'flyerdesign' },

    // Banner design mappings
    { printMpvId: 'retractableBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'vinylBanners', serviceMpvId: 'bannerDesign' },

    // Business card design mappings
    { printMpvId: 'standardBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'matteBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'roundedCornerBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'glossyBusinessCards', serviceMpvId: 'businessCardDesign' },

    // Postcard design mappings
    { printMpvId: 'postcards', serviceMpvId: 'postcardDesign' },

    // Brochure design mappings
    { printMpvId: 'bifoldbrochures', serviceMpvId: 'brochureDesign' },
    { printMpvId: 'foldedLeaflets', serviceMpvId: 'brochureDesign' },

    // Door hanger design mappings
    { printMpvId: 'doorHanger', serviceMpvId: 'doorHangerDesign' },
    { printMpvId: 'doorHangers', serviceMpvId: 'doorHangerDesign' },

    // Poster design mappings
    { printMpvId: 'posters', serviceMpvId: 'posterDesign' },

    // Yard sign design mappings
    { printMpvId: 'yardSigns', serviceMpvId: 'yardSignDesign' },
    { printMpvId: 'lawnSigns', serviceMpvId: 'yardSignDesign' },
];

/**
 * Gets the service MPV ID for a given print MPV ID.
 * Comparison is case-insensitive as per requirements.
 *
 * @param printMpvId - The print MPV ID to map
 * @returns The corresponding service MPV ID, or undefined if no mapping exists
 */
export function getServiceMpvId(printMpvId: string): string | undefined {
    const mapping = OVERFLOW_PRODUCTS_MAPPING.find(
        (item) => item.printMpvId.toLowerCase() === printMpvId.toLowerCase()
    );
    return mapping?.serviceMpvId;
}

/**
 * Gets all print MPV IDs that map to a specific service MPV ID.
 * Useful for reverse lookups and validation.
 *
 * @param serviceMpvId - The service MPV ID to find print MPV IDs for
 * @returns Array of print MPV IDs that map to the given service MPV ID
 */
export function getPrintMpvIdsForService(serviceMpvId: string): string[] {
    return OVERFLOW_PRODUCTS_MAPPING.filter(
        (item) => item.serviceMpvId.toLowerCase() === serviceMpvId.toLowerCase()
    ).map((item) => item.printMpvId);
}

/**
 * Gets all eligible service MPV IDs from the mapping.
 * This is useful for feature flag checks and validation.
 *
 * @returns Array of unique service MPV IDs
 */
export function getEligibleServiceMpvIds(): string[] {
    const serviceIds = OVERFLOW_PRODUCTS_MAPPING.map((item) => item.serviceMpvId);
    return [...new Set(serviceIds)];
}

/**
 * Gets all eligible print MPV IDs from the mapping.
 * This is useful for feature flag checks and validation.
 *
 * @returns Array of print MPV IDs
 */
export function getEligiblePrintMpvIds(): string[] {
    return OVERFLOW_PRODUCTS_MAPPING.map((item) => item.printMpvId);
}

/**
 * Checks if a print MPV ID is eligible for overflow feature flag.
 *
 * @param printMpvId - The print MPV ID to check
 * @returns True if the print MPV ID has a corresponding service mapping
 */
export function isPrintMpvIdEligible(printMpvId: string): boolean {
    return getServiceMpvId(printMpvId) !== undefined;
}

/**
 * Checks if a service MPV ID is eligible for overflow feature flag.
 *
 * @param serviceMpvId - The service MPV ID to check
 * @returns True if the service MPV ID exists in the mapping
 */
export function isServiceMpvIdEligible(serviceMpvId: string): boolean {
    return getEligibleServiceMpvIds().some((id) => id.toLowerCase() === serviceMpvId.toLowerCase());
}
